/**
 * Load a floor plan from a user file into something the importer can work with.
 *
 * Four families, in descending order of how much the file already knows:
 *
 *   3D MODEL   GLB / glTF / OBJ. Cut a horizontal section and draw it; the
 *              model's own extent gives the scale outright.
 *   VECTOR     DXF / SVG / GeoJSON. Exact coordinates. Closed shapes become
 *              regions directly with no tracing error at all; linework-only
 *              drawings are rasterised and traced like anything else.
 *   PDF        rasterised with pdf.js, reusing the Digital Twin's renderer
 *              rather than standing up a second worker config — that helper
 *              already points at the worker copied into public/ at build, so it
 *              works offline and behind the /v3.0 sub-path.
 *   IMAGE      PNG / JPG / WebP. Traced.
 *
 * Everything comes back in the same shape, so the rest of the importer never
 * has to care which door the plan came in through. `shapes` and `knownScale`
 * are the only extras, and both are optional enrichment.
 */

import { renderPdfToCanvas } from '../../DigitalTwin/pdfRender';
import {
    parseDxf, parseSvg, parseGeoJson, normaliseShapes, rasteriseShapes,
} from './vectorSource';
import { isModelFile, loadModel, sectionToPlan } from './modelSource';

/** Rasterise PDFs at this width; enough detail to trace, cheap enough to floodfill. */
const PDF_RASTER_WIDTH = 2000;

export const ACCEPTED = [
    '.png,.jpg,.jpeg,.webp,.gif,.bmp',
    '.pdf,application/pdf',
    '.dxf,.svg,.json,.geojson',
    '.glb,.gltf,.obj',
    'image/*',
].join(',');

const ext = (file) => {
    const m = /\.([a-z0-9]+)$/i.exec(file.name || '');
    return m ? m[1].toLowerCase() : '';
};

const VECTOR_PARSERS = {
    dxf: parseDxf,
    svg: parseSvg,
    json: parseGeoJson,
    geojson: parseGeoJson,
};

export function isPdf(file) {
    return file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
}

function loadImageElement(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('That image could not be decoded.'));
        img.src = url;
    });
}

/**
 * @returns {Promise<{ source: CanvasImageSource, width, height, previewUrl, kind }>}
 *   `source` is drawable into a canvas; `previewUrl` is safe to put in an <img>.
 */
export async function loadPlanFile(file, options = {}) {
    if (!file) throw new Error('No file selected.');

    // ── 3D model ────────────────────────────────────────────────────────────
    if (isModelFile(file)) {
        const root = await loadModel(file);
        const cut = sectionToPlan(root, options.model);
        return {
            source: cut.canvas,
            width: cut.width,
            height: cut.height,
            previewUrl: cut.canvas.toDataURL('image/png'),
            kind: 'model',
            // The model is in real units, so the sheet's width in metres is
            // already known and calibration becomes a confirmation.
            knownScale: { widthM: cut.spanX, heightM: cut.spanZ, source: 'model units' },
            model: { segments: cut.segments, cutY: cut.cutY, size: cut.modelSize },
        };
    }

    // ── vector drawing ──────────────────────────────────────────────────────
    const parser = VECTOR_PARSERS[ext(file)];
    if (parser) {
        const parsed = parser(await file.text());
        const normalised = normaliseShapes(parsed.shapes);
        if (!normalised) throw new Error('That drawing has no geometry in it.');

        const canvas = rasteriseShapes(normalised.shapes);
        const closed = normalised.shapes.filter((sh) => sh.closed).length;
        return {
            source: canvas,
            width: canvas.width,
            height: canvas.height,
            previewUrl: canvas.toDataURL('image/png'),
            kind: 'vector',
            format: ext(file),
            // Handed to the Detect step: with closed shapes there is nothing to
            // trace, because the drawing already states where the rooms are.
            shapes: normalised.shapes,
            closedShapes: closed,
            // A DXF is drawn in real units; SVG and GeoJSON are not, so only the
            // extent is reported and the operator still sets the scale.
            knownScale: parsed.units === 'drawing'
                ? { widthM: normalised.extent.span, heightM: normalised.extent.span, source: 'DXF drawing units' }
                : null,
        };
    }

    if (isPdf(file)) {
        const buffer = await file.arrayBuffer();
        const canvas = document.createElement('canvas');
        // pdfRender takes a pdf.js source; a copied buffer avoids the detach
        // pdf.js performs on the ArrayBuffer it is handed.
        await renderPdfToCanvas({ data: new Uint8Array(buffer.slice(0)) }, canvas, PDF_RASTER_WIDTH);
        return {
            source: canvas,
            width: canvas.width,
            height: canvas.height,
            previewUrl: canvas.toDataURL('image/png'),
            kind: 'pdf',
        };
    }

    const objectUrl = URL.createObjectURL(file);
    try {
        const img = await loadImageElement(objectUrl);
        // Bake to a canvas so the object URL can be released immediately and the
        // preview survives a re-render without holding a blob handle open.
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        return {
            source: canvas,
            width: canvas.width,
            height: canvas.height,
            previewUrl: canvas.toDataURL('image/png'),
            kind: 'image',
        };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}
